import { Box } from '@mui/material';
import _ from 'lodash';
import React, { useMemo, useState } from 'react';
import FilterSidebar from '../components/common/filters/FilterSidebar';
import { FilterState } from '../components/common/filters/interfaces';
import PreferencePill from '../components/preferences/PreferencePill';
import { RolePillsCell } from '../components/preferences/RolePillsCell';
import { useDownloadCsv } from '../hooks/useDownloadCsv';
import { useSearchTerm } from '../hooks/useSearchTerm';
import { useSyncPageParams } from '../hooks/useSyncPageParams';
import { useUserSorts } from '../hooks/useUserSorts';
import { EmployeeAvailableColumns, EmployeeSortParam, useGetAllEmployees, UseGetAllEmployeesParams } from '../services/react-query/employees';
import { useGetAllPreferences } from '../services/react-query/preferences';
import { listPageLayoutStyles, listPageTableWrapperStyles } from '../styles/sxStyling';
import { Employee } from '../types/Employee';
import { PREFERENCES_VALID_KEYS } from '../types/Preference';
import { UserConfigurableModule } from '../types/User';
import { Loading } from '../uikit';
import ITable, { AvailableColumn } from '../uikit/itable/ITable';
import { EmployeeColumns, EmployeeFilters } from '../utility/employee';
import ListHeaderActions from '../components/listHeader/ListHeaderActions';
import ListHeader from '../components/listHeader/ListHeader';
import { useNavigate } from 'react-router-dom';
import { useGetAllEmployeeTemplates } from '../services/react-query/employee_templates';
import qs from 'qs';
import TemplateModal from '../components/common/TemplateModal/TemplateModal';

const EmployeesList: React.FC = () => {
  const navigate = useNavigate();
  const { currentPage, setCurrentPage } = useSyncPageParams();
  const [filterState, setFilterState] = useState<FilterState>(undefined);
  const { userSorts } = useUserSorts<EmployeeSortParam>(UserConfigurableModule.EMPLOYEES);
  const [showModal, setShowModal] = useState(false);

  const { data: templates } = useGetAllEmployeeTemplates({
    params: { per_page: 0 },
  });
  const employeeTemplates = templates?.data ?? [];

  const { data: preferenceData } = useGetAllPreferences({
    params: { filters: { keys: [PREFERENCES_VALID_KEYS.EMPLOYEE_ROLE, PREFERENCES_VALID_KEYS.EMPLOYEE_TITLE] } },
  });
  const prefByUuidObj = useMemo(() => _.keyBy(preferenceData ?? [], 'uuid'), [preferenceData]);

  const baseParams = {
    search: useSearchTerm(),
    filters: filterState,
    sort: userSorts,
  };

  const queryParams: UseGetAllEmployeesParams = {
    ...baseParams,
    page: currentPage,
  };

  const { data, isLoading: loading } = useGetAllEmployees({ params: queryParams, enabled: true });

  const pillColumns: AvailableColumn<Employee, EmployeeAvailableColumns>[] = useMemo(() => {
    return EmployeeColumns.map((col) => {
      if (col.key === 'title_value') {
        return {
          ...col,
          render: (row: Employee) => {
            const pref = row.title_uuid ? prefByUuidObj[row.title_uuid] : undefined;
            return pref ? <PreferencePill {...pref} /> : <PreferencePill value={row.title_value ?? ''} color={null} />;
          },
        };
      }

      if (col.key === 'roles') {
        return {
          ...col,
          sortable: false,
          render: (row: Employee) => {
            const rolesUuids = _.isArray(row.roles_uuids) ? row.roles_uuids : [];
            return <RolePillsCell rolesUuids={rolesUuids} prefByUuid={prefByUuidObj} />;
          },
        };
      }
      return col;
    }) as AvailableColumn<Employee, EmployeeAvailableColumns>[];
  }, [prefByUuidObj]);

  const { isDownloadingCsv, handleDownloadCsv } = useDownloadCsv({
    params: baseParams,
    controller: UserConfigurableModule.EMPLOYEES,
  });

  const newWithTemplate = (templateUuid: string | null) => {
    const params: any = {};
    if (templateUuid) params.template = templateUuid;

    navigate(`./new?${qs.stringify(params)}`);
  };

  if (loading) return <Loading />;

  const employees = data?.data || [];
  const meta = data?.meta;

  const availableColumns = EmployeeColumns.map(({ key, label }) => ({
    key,
    label,
  }));

  return (
    <Box>
      <ListHeader
        title="Employees"
        rightActions={
          <ListHeaderActions
            module={UserConfigurableModule.EMPLOYEES}
            availableColumns={availableColumns}
            isDownloadingCsv={isDownloadingCsv}
            onDownloadCsv={handleDownloadCsv}
            onAdd={() => setShowModal(true)}
          />
        }
      />

      <Box sx={listPageLayoutStyles}>
        <FilterSidebar
          moduleFilters={EmployeeFilters(preferenceData)}
          module={UserConfigurableModule.EMPLOYEES}
          activeFilter={filterState}
          setActiveFilter={setFilterState}
        />

        <Box sx={listPageTableWrapperStyles}>
          <ITable<Employee, EmployeeAvailableColumns>
            availableColumns={pillColumns}
            data={employees}
            module={UserConfigurableModule.EMPLOYEES}
            page={currentPage}
            count={meta?.total_pages ?? 1}
            onPageChange={(_e, v) => setCurrentPage(v)}
          />
        </Box>
      </Box>
      <TemplateModal
        isOpen={showModal}
        onCancel={() => setShowModal(false)}
        onCreate={newWithTemplate}
        templates={employeeTemplates}
        title="Please choose which employee template to use"
      />
    </Box>
  );
};

export default EmployeesList;
